import { ComponentProps, useEffect } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import BaseUpsellStep from '../BaseUpsell/BaseUpsellStep';
import GenericUpsellContent from '../UpsellContent/GenericUpsellContent';
import getCtaText from '../getCtaText';

type Props = {
  contentsClassName?: string;
  heading: React.ReactNode;
  navigateTo: (modalKey: 'checkout' | 'upsell') => void;
  onCloseRequest: () => void;
  promoEligibleHeading: React.ReactNode;
  showFeatureList?: boolean;
  tierSelected: PurchaseTier;
  setTierSelected: (tier: PurchaseTier) => void;
  showPlusFirst?: boolean;
  upsellTrigger: ComponentProps<typeof BaseUpsellStep>['upsellTrigger'];
};

const GenericUpsellStep = ({
  heading,
  promoEligibleHeading,
  onCloseRequest,
  navigateTo,
  contentsClassName,
  tierSelected,
  setTierSelected,
  showPlusFirst,
  upsellTrigger
}: Props) => {
  const user = useUser();
  const experiments = useExperiments();
  const contentVariant = experiments['web-growth-rm-single-dual-tier-post-auth-upsell']?.value;
  const sendExposure = useExposureEvent('web-growth-rm-single-dual-tier-post-auth-upsell');

  useEffect(() => {
    if (!isSubscriber(user)) {
      sendExposure();
    }
  }, [sendExposure, user]);

  return (
    <BaseUpsellStep
      contentsClassName={contentsClassName}
      heading={heading}
      navigateTo={navigateTo}
      onCloseRequest={onCloseRequest}
      primaryButtonText={getCtaText(tierSelected, user?.isPromoEligible)}
      promoEligibleHeading={promoEligibleHeading}
      upsellTrigger={upsellTrigger}
      productType="peak"
      productsAvailable={user?.subscriptionTier === 'plus' ? 'peak' : 'plus-peak'}
    >
      <GenericUpsellContent
        contentVariant={contentVariant}
        upsellTrigger={upsellTrigger}
        promoEligibleHeading={promoEligibleHeading}
        heading={heading}
        tierSelected={tierSelected}
        setTierSelected={setTierSelected}
        showPlusFirst={showPlusFirst}
        subscriptionUser={user}
      />
    </BaseUpsellStep>
  );
};

export default GenericUpsellStep;
